import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import apiService from '../services/api';
import headerImage from '../assets/images/header.png';
import hcFlagMark from '../assets/images/homecrowd-flag-mark.svg';
import { getFanCardColors } from '../utils/fanCardColors';
import '../styles/PublicPages.css';

export const formatMemberSince = dateString => {
  if (!dateString) return null;
  const joinDate = new Date(dateString);
  if (Number.isNaN(joinDate.getTime())) return null;
  return joinDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatFanId = fanId => {
  const value = String(fanId || '').trim();
  return value || 'N/A';
};

const formatPoints = n => (n == null ? '—' : Number(n).toLocaleString('en-US'));

const setHeadMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const roundHalfAway = n => (n < 0 ? -Math.round(-n) : Math.round(n));
const isBackAngle = angle => Math.abs(roundHalfAway(angle / 180)) % 2 === 1;
const visualDeg = angle => angle - roundHalfAway(angle / 180) * 180;
const easeInOut = t => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

export const FlipCard = ({ profile, name, memberSince, crest, cardStyle, flat = false }) => {
  const wrapRef = useRef(null);
  const dragRef = useRef({
    pointerId: null,
    startX: 0,
    startY: 0,
    startAngle: 0,
    lastX: 0,
    lastT: 0,
    vx: 0,
    horizontal: false,
  });
  const fromRestRef = useRef(0);
  const angleRef = useRef(0);
  const animRef = useRef(0);
  const genRef = useRef(0);
  const showingBackRef = useRef(false);
  const [showingBack, setShowingBack] = useState(false);
  const [visual, setVisual] = useState(0);
  const [dragging, setDragging] = useState(false);

  const setFace = back => {
    showingBackRef.current = back;
    setShowingBack(back);
  };

  const paint = angle => {
    angleRef.current = angle;
    setVisual(visualDeg(angle));
  };

  const applyAngle = angle => {
    paint(angle);
    const back = isBackAngle(angle);
    if (back !== showingBackRef.current) setFace(back);
  };

  const settle = (angle, vx = 0) => {
    setDragging(false);
    const from = fromRestRef.current;
    const projected = angle + vx * 120;
    const direction = projected === from ? 0 : projected > from ? 1 : -1;
    const dest =
      direction !== 0 && Math.abs(projected - from) >= 50 ? from + direction * 180 : from;
    const gen = ++genRef.current;
    const back = isBackAngle(dest);
    setFace(back);
    fromRestRef.current = dest;
    const start = angleRef.current;
    const startedAt = performance.now();
    const tick = now => {
      if (gen !== genRef.current) return;
      const t = Math.min(1, (now - startedAt) / 220);
      paint(start + (dest - start) * easeInOut(t));
      if (t < 1) {
        animRef.current = requestAnimationFrame(tick);
        return;
      }
      const rest = back ? 180 : 0;
      angleRef.current = rest;
      fromRestRef.current = rest;
      setVisual(0);
      animRef.current = 0;
    };
    animRef.current = requestAnimationFrame(tick);
  };

  const onPointerDown = event => {
    if (event.button != null && event.button !== 0) return;
    genRef.current += 1;
    if (animRef.current) {
      cancelAnimationFrame(animRef.current);
      animRef.current = 0;
    }
    fromRestRef.current = roundHalfAway(angleRef.current / 180) * 180;
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startAngle: angleRef.current,
      lastX: event.clientX,
      lastT: event.timeStamp,
      vx: 0,
      horizontal: false,
    };
  };

  const onPointerMove = event => {
    const drag = dragRef.current;
    if (drag.pointerId !== event.pointerId) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (!drag.horizontal) {
      if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
      if (Math.abs(dx) <= Math.abs(dy)) {
        drag.pointerId = null;
        return;
      }
      drag.horizontal = true;
      event.currentTarget.setPointerCapture?.(event.pointerId);
      setDragging(true);
    }
    const dt = event.timeStamp - drag.lastT;
    if (dt > 0) drag.vx = (event.clientX - drag.lastX) / dt;
    drag.lastX = event.clientX;
    drag.lastT = event.timeStamp;
    const width = wrapRef.current?.offsetWidth || 1;
    const next = drag.startAngle + (dx / width) * 180;
    const max = fromRestRef.current + 180;
    const min = fromRestRef.current - 180;
    applyAngle(Math.max(min, Math.min(max, next)));
  };

  const onPointerUp = event => {
    const drag = dragRef.current;
    if (drag.pointerId !== event.pointerId) return;
    drag.pointerId = null;
    if (!drag.horizontal) return;
    settle(angleRef.current, drag.vx);
  };

  return (
    <div className="public-profile-flip-wrap">
      <div
        ref={wrapRef}
        className={`public-profile-flip${dragging ? ' is-dragging' : ''}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div
          className="public-profile-flip-inner"
          style={
            flat
              ? { transform: `scaleX(${Math.cos((visual * Math.PI) / 180)})` }
              : { transform: `perspective(1400px) rotateY(${visual}deg)` }
          }
        >
          <div
            className={`public-profile-card public-profile-card-face${
              showingBack ? ' is-hidden' : ''
            }`}
            style={cardStyle}
          >
            <div className="public-profile-card-texture" aria-hidden="true" />
            <div className="public-profile-card-top">
              <h1 className="public-profile-name">{name}</h1>
              {crest ? (
                <img src={crest} alt="" draggable={false} className="public-profile-crest" />
              ) : null}
            </div>
            <div className="public-profile-card-bottom">
              <dl className="public-profile-stats">
                <div>
                  <dt>Fan ID</dt>
                  <dd>{formatFanId(profile.fan_id)}</dd>
                </div>
                {memberSince ? (
                  <div>
                    <dt>Member since</dt>
                    <dd className="public-profile-stat-value--book">{memberSince}</dd>
                  </div>
                ) : null}
              </dl>
              <img src={hcFlagMark} alt="" draggable={false} className="public-profile-flag" />
            </div>
          </div>
          <div
            className={`public-profile-card public-profile-card-face${
              showingBack ? '' : ' is-hidden'
            }`}
            style={cardStyle}
          >
            <div className="public-profile-card-texture" aria-hidden="true" />
            <div className="public-profile-card-top">
              <h1 className="public-profile-name">
                {name}
                <span className="public-profile-name-sub">Fan Stats</span>
              </h1>
            </div>
            <div className="public-profile-card-bottom">
              <dl className="public-profile-back-stats">
                <div>
                  <dt>Lifetime points</dt>
                  <dd>{formatPoints(profile.lifetime_points)}</dd>
                </div>
                <div>
                  <dt>Transactions</dt>
                  <dd>{formatPoints(profile.transaction_count)}</dd>
                </div>
              </dl>
              <img src={hcFlagMark} alt="" draggable={false} className="public-profile-flag" />
            </div>
          </div>
        </div>
      </div>
      <div className="public-profile-dots">
        <button
          type="button"
          className={`public-profile-dot${!showingBack ? ' is-active' : ''}`}
          aria-label="Card front"
          onClick={() => settle(0)}
        />
        <button
          type="button"
          className={`public-profile-dot${showingBack ? ' is-active' : ''}`}
          aria-label="Fan stats"
          onClick={() => settle(180)}
        />
      </div>
    </div>
  );
};

const PublicProfile = () => {
  const { fanId } = useParams();
  const [status, setStatus] = useState('loading');
  const [profile, setProfile] = useState(null);
  const [lockupUrl, setLockupUrl] = useState('');
  const [cardPrimary, setCardPrimary] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const lookup = String(fanId || '').trim();
      if (!lookup) {
        setStatus('missing');
        return;
      }
      setStatus('loading');
      setLockupUrl('');
      setCardPrimary('');
      try {
        const data = await apiService.getPublicProfile(lookup);
        if (cancelled) return;
        const schoolId = data?.active_school?.id;
        let nextLockup = '';
        let nextColor = '';
        if (schoolId) {
          try {
            const branding = await apiService.getPublicSchoolBranding(schoolId);
            if (cancelled) return;
            nextLockup = String(branding?.headerLogoUrl || '').trim();
            nextColor = String(
              branding?.primaryColor ||
                branding?.button ||
                branding?.buttonColor ||
                branding?.buttonFillColor ||
                ''
            ).trim();
          } catch (_brandingError) {
            nextLockup = '';
            nextColor = '';
          }
        }
        if (cancelled) return;
        setProfile(data);
        setLockupUrl(nextLockup);
        setCardPrimary(nextColor);
        setStatus('ready');
      } catch (_error) {
        if (cancelled) return;
        setProfile(null);
        setLockupUrl('');
        setCardPrimary('');
        setStatus('missing');
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [fanId]);

  const name = `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || 'Member';
  const memberSince = formatMemberSince(profile?.date_joined);
  const school = profile?.active_school;
  const crest = school?.image || '';
  const palette = useMemo(() => getFanCardColors(cardPrimary), [cardPrimary]);
  const cardStyle = useMemo(
    () => ({
      background: `linear-gradient(131.956deg, ${palette.from} 38.927%, ${palette.to} 102.57%)`,
      borderColor: palette.border,
    }),
    [palette]
  );

  useEffect(() => {
    if (status !== 'ready') return undefined;
    const title = `${name} · HomeCrowd`;
    const previousTitle = document.title;
    const image = crest;
    document.title = title;
    setHeadMeta('property', 'og:title', title);
    setHeadMeta('property', 'og:image', image);
    setHeadMeta('property', 'og:url', window.location.href);
    setHeadMeta('name', 'twitter:image', image);
    return () => {
      document.title = previousTitle;
    };
  }, [status, name, crest]);

  return (
    <div className="public-profile-page">
      <div className="public-profile-inner">
        {status === 'loading' ? (
          <p className="public-profile-status">Loading profile...</p>
        ) : status === 'missing' || !profile ? (
          <p className="public-profile-status">This profile is not available.</p>
        ) : (
          <>
            <FlipCard
              profile={profile}
              name={name}
              memberSince={memberSince}
              crest={crest}
              cardStyle={cardStyle}
            />
            <div className="public-profile-lockup">
              <img
                src={lockupUrl || headerImage}
                alt=""
                className={
                  lockupUrl
                    ? 'public-profile-lockup-mark public-profile-lockup-mark--brand'
                    : 'public-profile-lockup-mark'
                }
              />
              {lockupUrl ? (
                <div className="legal-powered-by">
                  <span className="legal-powered-by-label">POWERED BY</span>
                  <span className="legal-powered-by-logo" role="img" aria-label="Homecrowd" />
                </div>
              ) : null}
            </div>
            {Array.isArray(profile.badges) && profile.badges.length ? (
              <div className="public-profile-achievements">
                <p className="public-profile-achievements-title">Achievements</p>
                <div className="public-profile-achievements-row">
                  {[...profile.badges]
                    .sort((a, b) => Number(!a.image_url) - Number(!b.image_url))
                    .map(badge => (
                    <div key={badge.slug} className="public-profile-achievement">
                      <span className="public-profile-achievement-mark">
                        {badge.image_url ? (
                          <img src={badge.image_url} alt="" />
                        ) : (
                          <span className="public-profile-achievement-empty" />
                        )}
                      </span>
                      <span className="public-profile-achievement-label">{badge.title}</span>
                      {badge.description ? (
                        <span className="public-profile-achievement-sub">{badge.description}</span>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default PublicProfile;
